import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  Typography,
  Divider,
  FormControlLabel,
  Checkbox,
  MenuItem,
} from '@mui/material';
import { Event, AccessTime, People, LocationOn, Repeat } from '@mui/icons-material';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { useTranslation } from 'react-i18next';
import { ConferenceRoom, Reservation, ReservationFormData } from './types';

export const MAX_WEEKLY_RECURRING_WEEKS = 8;

/** Derive series end date (local calendar day) from weeks count. */
export function recurringEndFromWeeks(
  startDate: Date,
  pattern: 'daily' | 'weekly',
  weeks: number
): Date {
  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);
  if (pattern === 'daily') {
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    return end;
  }
  const n = Math.min(MAX_WEEKLY_RECURRING_WEEKS, Math.max(1, Math.floor(weeks) || 1));
  const end = new Date(start);
  end.setDate(end.getDate() + (n - 1) * 7);
  return end;
}

interface ReservationFormDialogProps {
  open: boolean;
  editingReservation: Reservation | null;
  formData: ReservationFormData;
  rooms: ConferenceRoom[];
  notice: { type: 'success' | 'error'; text: string } | null;
  onClose: () => void;
  onSave: () => void;
  onCancelReservation?: () => void;
  onFormChange: (data: Partial<ReservationFormData>) => void;
  onClearNotice: () => void;
  canDelete?: boolean;
}

const ReservationFormDialog: React.FC<ReservationFormDialogProps> = ({
  open,
  editingReservation,
  formData,
  rooms,
  notice,
  onClose,
  onSave,
  onCancelReservation,
  onFormChange,
  onClearNotice,
  canDelete,
}) => {
  const { t } = useTranslation();
  const roomName = rooms.find((r) => r.id === formData.roomId)?.name || 'Stiklainis';
  const isDaily = formData.recurringPattern === 'daily';
  const weeksValue = isDaily ? 1 : Math.min(MAX_WEEKLY_RECURRING_WEEKS, Math.max(1, formData.recurringWeeks || 1));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        {editingReservation ? t('stiklainis.editReservation') : t('stiklainis.createNewReservation')}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          {notice && (
            <Alert severity={notice.type} sx={{ mb: 2 }} onClose={onClearNotice}>
              {notice.text}
            </Alert>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Event color="primary" fontSize="small" />
            <Typography variant="subtitle2" color="text.secondary">
              {t('stiklainis.title')}
            </Typography>
          </Box>
          <TextField
            fullWidth
            label={t('stiklainis.title')}
            value={formData.title}
            onChange={(e) => onFormChange({ title: e.target.value })}
            required
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label={t('stiklainis.description')}
            multiline
            rows={3}
            value={formData.description}
            onChange={(e) => onFormChange({ description: e.target.value })}
            sx={{ mb: 2 }}
          />

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <AccessTime color="primary" fontSize="small" />
            <Typography variant="subtitle2" color="text.secondary">
              {t('stiklainis.date')} / {t('stiklainis.startTime')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <Box sx={{ flex: '1 1 180px' }}>
              <DatePicker
                label={t('stiklainis.date')}
                value={formData.startDate}
                onChange={(date) => onFormChange({ startDate: date || new Date() })}
                disablePast={!editingReservation}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Box>
            <Box sx={{ flex: '1 1 140px' }}>
              <TimePicker
                label={t('stiklainis.startTime')}
                value={formData.startTime}
                timeSteps={{ minutes: 15 }}
                skipDisabled
                onChange={(time) => {
                  if (!time) return;
                  const startOnDate = new Date(formData.startDate);
                  startOnDate.setHours(time.getHours(), time.getMinutes(), 0, 0);
                  const newEnd = new Date(startOnDate.getTime() + 30 * 60 * 1000);
                  onFormChange({ startTime: startOnDate, endTime: newEnd });
                }}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Box>
            <Box sx={{ flex: '1 1 140px' }}>
              <TimePicker
                label={t('stiklainis.endTime')}
                value={formData.endTime}
                timeSteps={{ minutes: 15 }}
                skipDisabled
                onChange={(time) => onFormChange({ endTime: time || new Date() })}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Box>
          </Box>

          {!editingReservation && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Repeat color="primary" fontSize="small" />
                <Typography variant="subtitle2" color="text.secondary">
                  {t('stiklainis.repeat', 'Kartoti')}
                </Typography>
              </Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.isRecurring}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      onFormChange({
                        isRecurring: checked,
                        recurringWeeks: checked
                          ? formData.recurringPattern === 'daily'
                            ? 1
                            : formData.recurringWeeks || 4
                          : formData.recurringWeeks,
                      });
                    }}
                  />
                }
                label={t('stiklainis.repeatMeeting', 'Kartoti susitikimą')}
              />
              {formData.isRecurring && (
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1, mb: 1 }}>
                  <TextField
                    select
                    label={t('stiklainis.repeatPattern', 'Dažnumas')}
                    value={formData.recurringPattern}
                    onChange={(e) => {
                      const pattern = e.target.value as 'daily' | 'weekly';
                      onFormChange({
                        recurringPattern: pattern,
                        recurringWeeks: pattern === 'daily' ? 1 : formData.recurringWeeks || 4,
                      });
                    }}
                    sx={{ flex: '1 1 160px' }}
                  >
                    <MenuItem value="weekly">{t('stiklainis.repeatWeekly', 'Kas savaitę')}</MenuItem>
                    <MenuItem value="daily">{t('stiklainis.repeatDaily', 'Kasdien')}</MenuItem>
                  </TextField>
                  <TextField
                    select
                    label={t('stiklainis.repeatWeeks', 'Savaičių skaičius')}
                    value={weeksValue}
                    disabled={isDaily}
                    onChange={(e) =>
                      onFormChange({ recurringWeeks: parseInt(String(e.target.value), 10) || 1 })
                    }
                    sx={{ flex: '1 1 160px' }}
                  >
                    {(isDaily ? [1] : Array.from({ length: MAX_WEEKLY_RECURRING_WEEKS }, (_, i) => i + 1)).map(
                      (n) => (
                        <MenuItem key={n} value={n}>
                          {n}
                        </MenuItem>
                      )
                    )}
                  </TextField>
                </Box>
              )}
              {formData.isRecurring && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  {isDaily
                    ? t(
                        'stiklainis.repeatDailyLimitHelp',
                        'Kasdien kartoti galima tik 1 savaitę (7 dienos).'
                      )
                    : t(
                        'stiklainis.repeatWeeklyLimitHelp',
                        'Kas savaitę galima kartoti nuo 1 iki 8 savaičių.'
                      )}
                </Typography>
              )}
            </>
          )}

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ flex: '1 1 200px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <LocationOn color="primary" fontSize="small" />
                <Typography variant="subtitle2" color="text.secondary">{t('stiklainis.room')}</Typography>
              </Box>
              <TextField fullWidth value={roomName} disabled />
            </Box>
            <Box sx={{ flex: '1 1 140px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <People color="primary" fontSize="small" />
                <Typography variant="subtitle2" color="text.secondary">{t('stiklainis.attendeeCount')}</Typography>
              </Box>
              <TextField
                fullWidth
                type="text"
                value={String(formData.attendeeCount ?? '')}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === '') {
                    onFormChange({ attendeeCount: '' as unknown as number | '' });
                    return;
                  }
                  if (/^\d+$/.test(raw)) {
                    onFormChange({ attendeeCount: raw as unknown as number | '' });
                  }
                }}
                onBlur={() => {
                  const raw = String(formData.attendeeCount ?? '');
                  const parsed = parseInt(raw, 10);
                  if (!Number.isFinite(parsed) || parsed < 1) {
                    onFormChange({ attendeeCount: 1 as unknown as number | '' });
                  } else {
                    onFormChange({ attendeeCount: parsed as unknown as number | '' });
                  }
                }}
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                required
              />
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
        <Box>
          {editingReservation && canDelete && onCancelReservation && (
            <Button color="error" onClick={onCancelReservation}>
              {t('stiklainis.cancelReservation')}
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose}>{t('common.close', 'Uždaryti')}</Button>
          <Button
            variant="contained"
            onClick={onSave}
            disabled={
              !formData.title.trim() ||
              (formData.isRecurring &&
                !editingReservation &&
                !(formData.recurringWeeks >= 1))
            }
          >
            {editingReservation ? t('stiklainis.update') : t('stiklainis.create')}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ReservationFormDialog;
